import React, { createContext, useState, useContext, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Cinematic screening room palette — theater-inspired
const cinematicPalette = {
  // Primary — warm gold/amber (theater marquee, projector light)
  primary: {
    light: '#f5c842',
    main: '#d4a017',
    dark: '#b8860b',
  },
  // Secondary — cool silver/blue (screen glow)
  secondary: {
    light: '#90a4ae',
    main: '#607d8b',
    dark: '#455a64',
  },
  // Status colors — refined, less saturated
  success: {
    light: '#66bb6a',
    main: '#43a047',
    dark: '#2e7d32',
  },
  info: {
    light: '#4fc3f7',
    main: '#039be5',
    dark: '#0277bd',
  },
  warning: {
    light: '#ffa726',
    main: '#ef6c00',
    dark: '#e65100',
  },
  error: {
    light: '#ef5350',
    main: '#c62828',
    dark: '#b71c1c',
  },
  background: {
    dark: {
      default: '#0a0c10',       // near-black theater
      paper: '#12151c',         // dark charcoal panels
      elevated: '#1a1e28',      // slightly lifted surfaces
    },
    light: {
      default: '#f5f5f0',       // warm off-white
      paper: '#ffffff',
      elevated: '#ffffff',
    },
  },
};

const getTheme = (mode) => createTheme({
  spacing: 8,
  palette: {
    mode,
    primary: cinematicPalette.primary,
    secondary: cinematicPalette.secondary,
    success: cinematicPalette.success,
    info: cinematicPalette.info,
    warning: cinematicPalette.warning,
    error: cinematicPalette.error,
    background: mode === 'dark'
      ? cinematicPalette.background.dark
      : cinematicPalette.background.light,
    text: {
      primary: mode === 'dark' ? '#e8e6e3' : '#1a1a1a',
      secondary: mode === 'dark' ? 'rgba(232, 230, 227, 0.6)' : 'rgba(26, 26, 26, 0.55)',
    },
    divider: mode === 'dark' ? 'rgba(212, 160, 23, 0.08)' : 'rgba(0, 0, 0, 0.08)',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica Neue", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.03em',
      fontSize: '2.25rem',
    },
    h2: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
      fontSize: '1.75rem',
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.015em',
      fontSize: '1.5rem',
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
      fontSize: '1.25rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.1rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '0.95rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
      letterSpacing: '0.01em',
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.8125rem',
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.75rem',
      letterSpacing: '0.02em',
    },
    overline: {
      letterSpacing: '0.1em',
      fontWeight: 600,
      fontSize: '0.65rem',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // Subtle vignette on the page edges in dark mode
          ...(mode === 'dark' && {
            backgroundImage: 'radial-gradient(ellipse at center, #0a0c10 0%, #060709 100%)',
          }),
        },
        // Custom scrollbar
        '*::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '*::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '*::-webkit-scrollbar-thumb': {
          background: mode === 'dark' ? 'rgba(212, 160, 23, 0.2)' : 'rgba(0,0,0,0.15)',
          borderRadius: '3px',
          '&:hover': {
            background: mode === 'dark' ? 'rgba(212, 160, 23, 0.35)' : 'rgba(0,0,0,0.25)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          ...(mode === 'dark' && {
            border: '1px solid rgba(212, 160, 23, 0.06)',
          }),
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          fontSize: '0.875rem',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
        sizeSmall: {
          padding: '4px 10px',
          fontSize: '0.8125rem',
          borderRadius: 6,
        },
        sizeLarge: {
          padding: '10px 24px',
          fontSize: '0.9375rem',
          borderRadius: 10,
        },
        contained: {
          boxShadow: mode === 'dark'
            ? '0 2px 8px rgba(212, 160, 23, 0.15)'
            : '0 2px 8px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: mode === 'dark'
              ? '0 4px 16px rgba(212, 160, 23, 0.25)'
              : '0 4px 12px rgba(0,0,0,0.15)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: mode === 'dark'
            ? '0 4px 16px rgba(0,0,0,0.4)'
            : '0 2px 8px rgba(0,0,0,0.06)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          border: mode === 'dark' ? '1px solid rgba(212, 160, 23, 0.06)' : '1px solid rgba(0,0,0,0.04)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: mode === 'dark'
              ? '0 8px 24px rgba(0,0,0,0.5), 0 0 0 1px rgba(212, 160, 23, 0.08)'
              : '0 8px 24px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          height: 26,
          fontSize: '0.75rem',
          fontWeight: 500,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: `1px solid ${mode === 'dark' ? 'rgba(212, 160, 23, 0.08)' : 'rgba(0,0,0,0.06)'}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${mode === 'dark' ? 'rgba(212, 160, 23, 0.08)' : 'rgba(0,0,0,0.06)'}`,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            transition: 'all 0.2s ease',
            fontSize: '0.875rem',
            '&:not(.MuiInputBase-multiline)': {
              height: 42,
            },
            '& input': {
              padding: '10px 14px',
              height: 'auto',
            },
            '&.Mui-focused': {
              ...(mode === 'dark' && {
                boxShadow: '0 0 0 2px rgba(212, 160, 23, 0.15)',
              }),
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          height: 42,
        },
        select: {
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          // Single-line inputs get the 42px shell so they line up with
          // Buttons and Selects. Multiline inputs (textareas) must grow
          // to fit their rows — forcing 42px there squashes the textarea
          // into a single visible line and the typed text climbs over
          // the floating label. v10.21.4 fix; pre-fix DCPLibrary worked
          // around with a local 'height: auto !important' sx override
          // (see DCPLibrary.jsx:1400). Now handled at the theme level.
          '&:not(.MuiInputBase-multiline)': {
            height: 42,
          },
          borderRadius: 8,
          '& .MuiOutlinedInput-input': {
            padding: '10px 14px',
            height: 'auto',
            // Kill Chrome's blue autofill background — paint it with the
            // current input bg via a 100px inset shadow and freeze the
            // transition so Chrome can't repaint it.
            '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
              WebkitBoxShadow: mode === 'dark'
                ? '0 0 0 100px #12151c inset'
                : '0 0 0 100px #ffffff inset',
              WebkitTextFillColor: mode === 'dark' ? '#e8e6e3' : '#0a0c10',
              caretColor: mode === 'dark' ? '#e8e6e3' : '#0a0c10',
              borderRadius: 'inherit',
              transition: 'background-color 9999s ease-in-out 0s',
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: mode === 'dark' ? 'rgba(232, 230, 227, 0.12)' : 'rgba(0, 0, 0, 0.15)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: mode === 'dark' ? 'rgba(212, 160, 23, 0.3)' : 'rgba(0, 0, 0, 0.3)',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: mode === 'dark'
              ? 'rgba(212, 160, 23, 0.04)'
              : 'rgba(0, 0, 0, 0.02)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
          borderBottom: `1px solid ${mode === 'dark' ? 'rgba(232, 230, 227, 0.06)' : 'rgba(0,0,0,0.06)'}`,
        },
        head: {
          fontWeight: 600,
          fontSize: '0.75rem',
          letterSpacing: '0.03em',
          textTransform: 'uppercase',
          color: mode === 'dark' ? 'rgba(232, 230, 227, 0.5)' : 'rgba(0,0,0,0.5)',
          backgroundColor: mode === 'dark'
            ? 'rgba(212, 160, 23, 0.03)'
            : 'rgba(0,0,0,0.02)',
        },
      },
    },
    MuiListItem: {
      defaultProps: {
        dense: true,
      },
      styleOverrides: {
        root: {
          paddingTop: 6,
          paddingBottom: 6,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: 8,
          borderRadius: 8,
          transition: 'all 0.15s ease',
          '&.MuiIconButton-sizeSmall': {
            padding: 4,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: '0.875rem',
          padding: '8px 16px',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: 4,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          ...(mode === 'dark' && {
            border: '1px solid rgba(212, 160, 23, 0.08)',
            boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
          }),
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          marginTop: 4,
          boxShadow: mode === 'dark'
            ? '0 8px 24px rgba(0,0,0,0.5), 0 0 0 1px rgba(212, 160, 23, 0.06)'
            : '0 8px 24px rgba(0,0,0,0.12)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          paddingTop: 8,
          paddingBottom: 8,
          borderRadius: 4,
          margin: '0 4px',
          '&:hover': {
            backgroundColor: mode === 'dark'
              ? 'rgba(212, 160, 23, 0.08)'
              : 'rgba(0,0,0,0.04)',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginTop: 4,
          marginBottom: 4,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
          '&:not(.MuiInputLabel-shrink)': {
            transform: 'translate(14px, 10px) scale(1)',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
          backgroundColor: mode === 'dark' ? 'rgba(232, 230, 227, 0.08)' : 'rgba(0,0,0,0.06)',
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '8px !important',
          '&:before': {
            display: 'none',
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: mode === 'dark'
              ? 'rgba(212, 160, 23, 0.03)'
              : 'rgba(0,0,0,0.02)',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${mode === 'dark' ? 'rgba(232, 230, 227, 0.06)' : 'rgba(0,0,0,0.06)'}`,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: mode === 'dark'
              ? 'rgba(212, 160, 23, 0.04)'
              : 'rgba(0,0,0,0.02)',
          },
        },
      },
    },
  },
});

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'dark'; // Default to dark
  });

  const getActualMode = () => {
    if (themeMode === 'system') {
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return themeMode;
  };

  const [actualMode, setActualMode] = useState(getActualMode());

  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
    setActualMode(getActualMode());
  }, [themeMode, getActualMode]);

  useEffect(() => {
    if (themeMode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => setActualMode(mediaQuery.matches ? 'dark' : 'light');

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [themeMode]);

  const setTheme = (mode) => {
    setThemeMode(mode);
  };

  const theme = getTheme(actualMode);

  return (
    <ThemeContext.Provider value={{ mode: actualMode, themeMode, setTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
